import React, { useRef, useState } from 'react'
import { Box, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { DndContext } from '@dnd-kit/core'
import { advancedColors } from '../../theme'
import CommentPopover, { CommentDraft } from './CommentPopover'
import { TagDecoration } from './ItemTag'
import ItemPicker, { PickerState } from './ItemPicker'
import PlanGridHead from './PlanGridHead'
import PlanRowLabel from './PlanRowLabel'
import PlannerCell, { PlacedItem } from './PlannerCell'
import { CarePlanActions } from './carePlanActions'
import { CarePlanState, SURVEYS_ROW_KEY } from './carePlanState'
import {
  MONTHS,
  MONTH_LABELS,
  TOTAL_WEEKS,
  TrackRow,
  WEEKS_PER_MONTH,
  categoriesFor,
  defaultMonthsFor,
  interactionFor,
  visibleRows,
} from './categories'
import { cellInputSx, dataCellSx } from './gridSx'
import { LABEL_COL_PX, PlanZoom, WEEK_COL_PX, itemStartWeek, nowLinePercent } from './livePlan'
import { usePlanDrag } from './usePlanDrag'

type Props = {
  state: CarePlanState
  actions: CarePlanActions
  editable: boolean
  trackRows?: TrackRow[]
  // Track rows are track management, not plan prose, so they don't wait for Edit mode (ADR-094).
  tracksEditable?: boolean
  // Live enrolments on the plan: status per planned track, plus tracks the plan never placed (ADR-092).
  decorationFor?: (rowKey: string, item: PlacedItem) => TagDecoration | undefined
  unplannedItemsFor?: (row: TrackRow, planned: PlacedItem[]) => PlacedItem[]
  titleFor?: (trackKey: string) => string
  currentMonth?: number | null
  // "Now", in week columns from Month 1's left edge (`nowColumn`).
  nowColumn?: number | null
  toolbar?: React.ReactNode
  // A drag that would move a bar to plan week `toWeek`; the caller runs `apply` to move it, or declines.
  onMove?: (rowKey: string, item: PlacedItem, toWeek: number, apply: () => void) => void
}

const CarePlanGrid = ({
  state,
  actions,
  editable,
  trackRows = [],
  tracksEditable = false,
  decorationFor,
  unplannedItemsFor,
  titleFor,
  currentMonth = null,
  nowColumn = null,
  toolbar,
  onMove,
}: Props) => {
  const [zoom, setZoom] = useState<PlanZoom>('month')
  const [pickerState, setPickerState] = useState<PickerState | null>(null)
  const [commentDraft, setCommentDraft] = useState<CommentDraft | null>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const drag = usePlanDrag({ zoom, tableRef, actions, trackRows, unplannedItemsFor, onMove })

  const expandedCategoryIds = state.expandedCategoryIds ?? []
  const rows = visibleRows(categoriesFor(trackRows), expandedCategoryIds)

  const handleAddClick = (rowKey: string, month: number) => (event: React.MouseEvent<HTMLElement>) => {
    const interaction = interactionFor(rowKey, trackRows)
    if (!interaction) {
      return
    }
    if (interaction.kind === 'single') {
      actions.placeItem(rowKey, month, interaction.label, interaction.months)
      return
    }
    setPickerState({
      rowKey,
      month,
      options:
        interaction.kind === 'tracks'
          ? interaction.tracks.map((track) => ({ label: track.title, trackKey: track.trackKey }))
          : actions.optionsFor(rowKey).map((label) => ({ label })),
      defaultMonths: defaultMonthsFor(rowKey),
      anchorEl: event.currentTarget,
    })
  }

  const handleCommentClick = (rowKey: string) => (item: PlacedItem, event: React.MouseEvent<HTMLElement>) =>
    setCommentDraft({ rowKey, itemId: item.id, anchorEl: event.currentTarget, text: item.comment ?? '' })

  const weekZoom = zoom === 'week'
  const frameWidth = weekZoom ? LABEL_COL_PX + TOTAL_WEEKS * WEEK_COL_PX : undefined

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5 }}>
        {toolbar}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={zoom}
          aria-label="Planner zoom"
          onChange={(_event, next: PlanZoom | null) => next && setZoom(next)}
        >
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <DndContext {...drag}>
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ position: 'relative', width: frameWidth ?? '100%' }}>
            <Box
              component="table"
              ref={tableRef}
              sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
            >
              <PlanGridHead weekZoom={weekZoom} currentMonth={currentMonth} />
              <Box component="tbody">
                {rows.map((row) => {
                  const expanded = row.categoryId ? expandedCategoryIds.includes(row.categoryId) : false
                  const interaction = interactionFor(row.key, trackRows)
                  const isSurveysRow = row.key === SURVEYS_ROW_KEY
                  const weekly = weekZoom && !isSurveysRow
                  const totalUnits = isSurveysRow || weekly ? TOTAL_WEEKS : MONTHS
                  const unitColSpan = isSurveysRow || weekly ? 1 : WEEKS_PER_MONTH
                  const trackRow = trackRows.find((candidate) => candidate.key === row.key)
                  const planned = actions.itemsIn(row.key).map((item) => ({
                    ...item,
                    option: item.trackKey && titleFor ? titleFor(item.trackKey) : item.option,
                  }))
                  const items = [
                    ...planned,
                    ...(trackRow && unplannedItemsFor ? unplannedItemsFor(trackRow, planned) : []),
                  ].sort((a, b) => a.startMonth - b.startMonth)
                  const layoutItems = weekly
                    ? items.map((item) => ({
                        ...item,
                        startMonth: itemStartWeek(item),
                        months: item.months * WEEKS_PER_MONTH,
                      }))
                    : items
                  const monthCells: React.ReactNode[] = []

                  if (interaction) {
                    let position = 0
                    while (position < totalUnits) {
                      const start = position
                      const itemsHere = layoutItems.filter((placed) => placed.startMonth === start)
                      const span =
                        itemsHere.length > 0
                          ? Math.min(Math.max(...itemsHere.map((item) => item.months)), totalUnits - start)
                          : 1
                      // An item starting inside a merged span renders in the covering cell.
                      const covered = layoutItems.filter(
                        (placed) => placed.startMonth > start && placed.startMonth < start + span
                      )
                      monthCells.push(
                        <PlannerCell
                          key={start}
                          rowKey={row.key}
                          month={start}
                          items={[...itemsHere, ...covered]}
                          colSpan={span * unitColSpan}
                          editable={trackRow ? editable || tracksEditable : editable}
                          decorationFor={decorationFor ? (item) => decorationFor(row.key, item) : undefined}
                          onAddClick={handleAddClick(row.key, weekly ? Math.floor(start / WEEKS_PER_MONTH) : start)}
                          onCommentClick={handleCommentClick(row.key)}
                          onRemoveClick={(item) => actions.removeItem(row.key, item.id)}
                          droppable={false}
                          startColumn={start * unitColSpan}
                        />
                      )
                      position = start + span
                    }
                  }

                  return (
                    <Box component="tr" key={row.key}>
                      <PlanRowLabel
                        row={row}
                        expanded={expanded}
                        onToggle={() => row.categoryId && actions.toggleCategory(row.categoryId)}
                      />
                      {interaction
                        ? monthCells
                        : MONTH_LABELS.map((_, monthIndex) => (
                            // eslint-disable-next-line react/no-array-index-key -- fixed-size grid, months are never reordered
                            <Box component="td" key={monthIndex} colSpan={WEEKS_PER_MONTH} sx={dataCellSx}>
                              <TextField
                                variant="standard"
                                fullWidth
                                disabled={!editable}
                                value={actions.cellAt(row.key, monthIndex)}
                                onChange={(event) => actions.setCell(row.key, monthIndex, event.target.value)}
                                InputProps={{ disableUnderline: true, sx: cellInputSx }}
                              />
                            </Box>
                          ))}
                    </Box>
                  )
                })}
              </Box>
            </Box>
            {nowColumn != null && (
              <Box
                role="img"
                aria-label="Current point in the plan"
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${nowLinePercent(nowColumn, zoom)}%`,
                  width: '2px',
                  backgroundColor: advancedColors.error,
                  pointerEvents: 'none',
                  zIndex: 2,
                  transform: 'translateX(-1px)',
                }}
              />
            )}
          </Box>
        </Box>
      </DndContext>

      <ItemPicker picker={pickerState} actions={actions} onClose={() => setPickerState(null)} />

      <CommentPopover
        draft={commentDraft}
        onChange={setCommentDraft}
        onSubmit={(draft) => actions.setItemComment(draft.rowKey, draft.itemId, draft.text)}
      />
    </>
  )
}

export default CarePlanGrid
